import {useMemo, useState, useCallback} from 'react';
import PropTypes from 'prop-types';
import {configureStore} from '@reduxjs/toolkit';
import {Provider} from 'react-redux';
import {library} from '@fortawesome/fontawesome-svg-core';
import {fab} from '@fortawesome/free-brands-svg-icons';
import {far} from '@fortawesome/free-regular-svg-icons';
import {fas} from '@fortawesome/free-solid-svg-icons';
import {Suspense} from 'react';

import applicationReducer, {initialState} from './slices/application';
import Printing from './components/Pages/Printing/Printing';
import OrderConfirm from './components/Pages/OrderConfirm/OrderConfirm';
import ErrorFallbackComponent from './components/ErrorFallback/ErrorFallback.jsx';
import {ThemeProvider} from './contexts/ThemeContext';
import ShadowDomWrapper from './components/ShadowDomWrapper/ShadowDomWrapper';
import {ScopedErrorBoundary} from './sentry';

library.add(fab, far, fas);

const Loader = () => <div>loading...</div>;

/**
 * Views for the HeatshrinkEditor
 */
const VIEWS = {
  PRINTING: 'printing',
  CONFIRM: 'confirm'
};

/**
 * HeatshrinkEditor - Federated component exposing the full heatshrink printing editor.
 * Uses state-based view routing instead of React Router to avoid conflicts with host apps.
 *
 * @param {Object} props
 * @param {string} props.uuid - User UUID (required)
 * @param {string} [props.siteCode='wc'] - Site code (deprecated, use theme prop instead)
 * @param {string} [props.designId] - Optional design ID to load
 * @param {Object} [props.theme] - Theme configuration object for site-specific styling.
 *   Can include colors, brand name, and custom content. See ThemeContext for shape.
 * @param {function} props.onEditingChange - Callback when editing state changes.
 *   Signature: (isEditing: boolean) => void
 * @param {function} props.onOrderSubmit - Callback when order is submitted.
 *   Signature: (orderData: object) => void
 *
 * Usage from host app:
 * ```jsx
 * import HeatshrinkEditor from 'hspFrontend/HeatshrinkEditor';
 *
 * // With default theme
 * <HeatshrinkEditor
 *   uuid="user-123"
 *   onEditingChange={(isEditing) => console.log('editing:', isEditing)}
 *   onOrderSubmit={(order) => console.log('order:', order)}
 * />
 *
 * // With custom theme
 * <HeatshrinkEditor
 *   uuid="user-123"
 *   theme={{
 *     name: 'Acme Corp',
 *     colors: {
 *       primary: '#ff5500',
 *       navBackground: '#333333',
 *       headerText: '#000000',
 *     }
 *   }}
 *   onOrderSubmit={(order) => console.log('order:', order)}
 * />
 * ```
 */
export default function HeatshrinkEditor({
  uuid,
  siteCode = 'wc',
  designId = '',
  theme,
  onEditingChange,
  onOrderSubmit
}) {
  // Create store with props-based initial state (memoized to prevent recreation)
  const store = useMemo(() => {
    const preloadedState = {
      application: {
        ...initialState,
        uuid,
        suppliedDesignId: designId,
        design: {
          ...initialState.design,
          siteCode
        }
      }
    };
    return configureStore({
      reducer: {application: applicationReducer},
      preloadedState,
      middleware: (getDefaultMiddleware) => getDefaultMiddleware({serializableCheck: false})
    });
  }, [uuid, siteCode, designId]);

  // State-based view management (no React Router)
  const [currentView, setCurrentView] = useState(VIEWS.PRINTING);
  const [confirmState, setConfirmState] = useState(null);

  // Navigate to confirm view with order state
  const handleNavigateToConfirm = useCallback((orderState) => {
    setConfirmState(orderState);
    setCurrentView(VIEWS.CONFIRM);
  }, []);

  // Navigate back to printing view
  const handleCancelConfirm = useCallback(() => {
    setCurrentView(VIEWS.PRINTING);
    setConfirmState(null);
  }, []);

  // Render current view based on state
  const renderView = () => {
    switch (currentView) {
      case VIEWS.CONFIRM:
        return (
          <OrderConfirm
            confirmState={confirmState}
            onCancel={handleCancelConfirm}
            onOrderSubmit={onOrderSubmit}
          />
        );
      case VIEWS.PRINTING:
      default:
        return (
          <Printing
            onNavigateToConfirm={handleNavigateToConfirm}
            onEditingChange={onEditingChange}
            onOrderSubmit={onOrderSubmit}
          />
        );
    }
  };

  return (
    <Provider store={store}>
      <ShadowDomWrapper>
        <ThemeProvider theme={theme}>
          <ScopedErrorBoundary fallback={<ErrorFallbackComponent />}>
            <Suspense fallback={<Loader />}>{renderView()}</Suspense>
          </ScopedErrorBoundary>
        </ThemeProvider>
      </ShadowDomWrapper>
    </Provider>
  );
}

HeatshrinkEditor.propTypes = {
  uuid: PropTypes.string.isRequired,
  siteCode: PropTypes.string,
  designId: PropTypes.string,
  theme: PropTypes.shape({
    name: PropTypes.string,
    colors: PropTypes.shape({
      // Core palette
      primary: PropTypes.string,
      primaryDark: PropTypes.string,
      // Navigation (most derive from primaryDark)
      navBackground: PropTypes.string,
      navDivider: PropTypes.string,
      navText: PropTypes.string,
      navActiveBackground: PropTypes.string,
      navActiveText: PropTypes.string,
      // General
      headerText: PropTypes.string,
      modalHeaderBackground: PropTypes.string,
      // Primary button (derives from primaryDark/primary)
      buttonPrimary: PropTypes.string,
      buttonPrimaryText: PropTypes.string,
      buttonPrimaryHover: PropTypes.string,
      // Outline button
      buttonOutlineBackground: PropTypes.string,
      buttonOutlineBorder: PropTypes.string,
      buttonOutlineText: PropTypes.string,
      buttonOutlineHoverBackground: PropTypes.string,
      buttonOutlineHoverBorder: PropTypes.string,
      buttonOutlineHoverText: PropTypes.string,
    }),
    content: PropTypes.shape({
      footer: PropTypes.node,
      disclaimer: PropTypes.node,
    }),
  }),
  onEditingChange: PropTypes.func,
  onOrderSubmit: PropTypes.func
};
